<template>
  <div class="modal-container" :style="{ minHeight: minimizedItems.length === 0 ? 0 : null }">
    <template v-for="componentData in currentOrgComponents">
      <component
        :key="componentData.id"
        :is="componentData.component"
        v-bind="componentData.props"
        :modal-data="{
          id: componentData.id,
          state: componentData.state,
          stackOrder: componentData.stackOrder
        }"
        v-on="componentData.on"
        @hidden="onHide(componentData)"
        @hide="unstackIfAny"
      ></component>
    </template>
    <minimized-items-bar :org-id="orgId" :minimized-items="minimizedItems" />
  </div>
</template>

<script>
import { trackEvent } from '@/analytics'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import { ENTITY_TYPE } from '@/constants/entities'
import events from '@/constants/events'
import { VISIBILITY_STATE } from '@/constants/minimize'
import * as R from 'ramda'
import EventBusMixin from '@/mixins/EventBusMixin'
import MinimizedItemsBar from '@/components/MinimizedItemsBar'

/**
 * This component renders modals
 *
 * It listens to event - RENDER_MODAL which is emitted by the component that wants to show a modal. Modal details (component, props, on handlers) comes as payload.
 */
export default {
  name: 'ModalsContainer',
  mixins: [EventBusMixin],
  props: ['orgId'],
  data() {
    return {
      components: []
    }
  },
  components: {
    MinimizedItemsBar
  },
  computed: {
    minimizedItems() {
      return this.components
        .filter(x => x.state === VISIBILITY_STATE.MINIMIZED && +x.orgId === +this.orgId)
        .map(x => x.minimizationConfig)
    },
    currentOrgComponents() {
      return this.components.filter(x => +x.orgId === +this.orgId)
    }
  },
  methods: {
    onHide(component) {
      this.components = this.components.filter(x => x.id !== component.id)
      const onHide = R.path(['on', 'onHide'], component)
      onHide && onHide()
    },
    unstackIfAny() {
      const stackedIndex = this.components.findLastIndex(x => x.state === VISIBILITY_STATE.STACKED)
      if (stackedIndex > -1) {
        const unstackComponent = {
          ...this.components[stackedIndex],
          state: VISIBILITY_STATE.MAXIMIZED
        }
        this.components = R.assocPath([stackedIndex], unstackComponent, this.components)
      }
    },
    trackEvent(action, { type }) {
      let eventName
      if (type === ENTITY_TYPE.KEY_RESULT) {
        eventName = EVENT_NAME.KR
      } else if (type === ENTITY_TYPE.INITIATIVE) {
        eventName = EVENT_NAME.INITIATIVE
      }
      if (eventName) {
        trackEvent(EVENT_CATEGORY.NAVIGATION, action, eventName)
      }
    },
    onMinimize(minimizationConfig) {
      const index = this.components.findIndex(R.propEq('id', minimizationConfig._id))
      const component = {
        ...this.components[index],
        state: VISIBILITY_STATE.MINIMIZED,
        minimizationConfig
      }
      // update component in the list and move it to the top
      const getUpdatedComponents = R.compose(
        R.move(index, -1),
        R.update(index, component)
      )
      this.components = getUpdatedComponents(this.components)
      this.trackEvent(EVENT_ACTION.DOCK, minimizationConfig)
    },
    onMaximize({ _id }) {
      const index = this.components.findIndex(R.propEq('id', _id))
      const component = {
        ...this.components[index],
        state: VISIBILITY_STATE.MAXIMIZED
      }
      this.components = R.update(index, component, this.components)
    },
    addModal(component) {
      const itemIndex = this.components.findIndex(R.propEq('id', component.id))
      if (itemIndex > -1) {
        const currentComponent = this.components[itemIndex]
        currentComponent.state = VISIBILITY_STATE.MAXIMIZED
        this.components = R.move(itemIndex, -1, this.components)
        this.eventBusEmit(events.MODAL_MAXIMIZE, currentComponent.minimizationConfig)
      } else {
        const maximizedModalIndex = this.components.findIndex(
          x => x.state === VISIBILITY_STATE.MAXIMIZED
        )
        let stackedCount = this.components.filter(x => x.state === VISIBILITY_STATE.STACKED).length
        if (maximizedModalIndex > -1) {
          const stackedModal = {
            ...this.components[maximizedModalIndex],
            state: VISIBILITY_STATE.STACKED,
            stackOrder: stackedCount++
          }
          this.components = R.assocPath([maximizedModalIndex], stackedModal, this.components)
        }
        this.components.push({
          ...component,
          state: VISIBILITY_STATE.MAXIMIZED,
          orgId: this.orgId,
          stackOrder: stackedCount
        })
      }
    }
  },
  mounted() {
    this.listenToEventBus(events.RENDER_MODAL, this.addModal)
    this.listenToEventBus(events.MODAL_MINIMIZE, this.onMinimize)
    this.listenToEventBus(events.MODAL_MAXIMIZE, this.onMaximize)
  },
  watch: {
    $route() {
      // on browser back, destroy opened modals
      this.components = this.components.filter(x => x.state !== VISIBILITY_STATE.MAXIMIZED)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.modal-container {
  min-height: 84px;
  background: $documentColor;
  display: flex;
  justify-content: center;
}
</style>
